import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export interface InventoryLimit {
  id: string;
  offer_id: string;
  room_type_id: string;
  room_rate_id?: string;
  date: string;
  total: number;
  count: number;
}

export type InventoryLimitMode = 'room_type' | 'room_rate' | null;

export const offerInventoryLimitsQueryKeys = {
  all: ['offerInventoryLimits'] as const,
  forRoomType: (offerId: string, propertyId: string, roomTypeId: string) =>
    [...offerInventoryLimitsQueryKeys.all, offerId, propertyId, roomTypeId] as const,
};

// Always fetched unscoped (no roomRateId) - the response carries `mode` (this room
// type's mode: room_type / room_rate / null, mutually exclusive) plus every limit
// row for the room type. In room-rate mode that's rows for every capped rate at
// once, so per-rate views are derived client-side from `limits` rather than
// requesting a room-rate-scoped subset from the server.
export function useGetOfferInventoryLimits(offerId: string, propertyId: string, roomTypeId: string) {
  return useQuery({
    queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
    queryFn: async ({ signal }) => {
      const response = await ReservationService.getOfferInventoryLimits(offerId, propertyId, roomTypeId, undefined, {
        signal,
      });
      return {
        limits: (response?.result?.limits ?? []) as Array<InventoryLimit>,
        mode: (response?.result?.type ?? null) as InventoryLimitMode,
      };
    },
    enabled: !!offerId && !!propertyId && !!roomTypeId,
  });
}

export function useSetOfferInventoryLimits(
  offerId: string,
  propertyId: string,
  roomTypeId: string,
  roomRateId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { start_date: string; end_date: string; total: number }) =>
      ReservationService.setOfferInventoryLimits(offerId, propertyId, roomTypeId, body, roomRateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
      });
    },
  });
}

export function useSetBulkOfferInventoryLimits(
  offerId: string,
  propertyId: string,
  roomTypeId: string,
  roomRateId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { dates: Array<string>; total: number }) =>
      ReservationService.setBulkOfferInventoryLimits(offerId, propertyId, roomTypeId, body, roomRateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
      });
    },
  });
}

export function useDeleteOfferInventoryLimits(
  offerId: string,
  propertyId: string,
  roomTypeId: string,
  roomRateId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params?: { start_date?: string; end_date?: string }) =>
      ReservationService.deleteOfferInventoryLimits(
        offerId,
        propertyId,
        roomTypeId,
        params?.start_date,
        params?.end_date,
        roomRateId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
      });
    },
  });
}

// Deletes a list of (possibly non-contiguous) dates in a single request instead of
// one per date - one invalidation/refetch instead of N.
export function useBulkDeleteOfferInventoryLimits(
  offerId: string,
  propertyId: string,
  roomTypeId: string,
  roomRateId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dates: Array<string>) =>
      ReservationService.bulkDeleteOfferInventoryLimits(offerId, propertyId, roomTypeId, dates, roomRateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: offerInventoryLimitsQueryKeys.forRoomType(offerId, propertyId, roomTypeId),
      });
    },
  });
}
